/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { Department, PrepListSummary } from "../shared/types";
import { DepartmentTabs } from "../shared/DepartmentTabs";
import { localDate, scheduleDays, weekdayNames } from "../shared/dates";
import { PrepListEditor } from "./PrepListEditor";
import { PrepRunner } from "./PrepRunner";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

// Prep lists: what to have ready before service, with pars.
// See docs/plans/workday/readme.md.
export const WorkDayPrepPage = defineComponent({
  name: "WorkDayPrepPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const names = weekdayNames("short");

    const loading = ref(true);
    const lists = ref<PrepListSummary[]>([]);
    const department = ref<Department | null>(null);
    const editing = ref<number | null | undefined>(undefined);
    const running = ref<number | null>(null);

    const load = async () => {
      try {
        lists.value =
          (await trpc.workday.prep.list.query()) as PrepListSummary[];
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };
    onMounted(load);

    const shown = computed(() =>
      lists.value.filter(
        (l) => !department.value || l.department === department.value,
      ),
    );

    const remove = async (l: PrepListSummary) => {
      if (!confirm(t("workday.prep.delete_confirm").replace("{name}", l.name)))
        return;
      await trpc.workday.prep.delete.mutate({ id: l.id });
      await load();
    };

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("workday.nav.prep")}
          subtitle={t("workday.prep.subtitle")}
        >
          <button
            class="btn btn-primary btn-sm"
            onClick={() => (editing.value = null)}
          >
            <span class="material-symbols-outlined text-base">add</span>
            {t("workday.prep.new")}
          </button>
        </SubpageHeader>
        <div class="flex-1 space-y-6 overflow-auto p-5">
          {lists.value.length > 0 && (
            <DepartmentTabs
              value={department.value}
              used={[...new Set(lists.value.map((l) => l.department))]}
              onChange={(d: Department | null) => (department.value = d)}
            />
          )}
          {loading.value ? (
            <div class="flex justify-center p-10">
              <Loading />
            </div>
          ) : lists.value.length === 0 ? (
            <div class="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-base-300 bg-base-100 px-6 py-16 text-center">
              <span class="material-symbols-outlined text-5xl text-primary">
                skillet
              </span>
              <h2 class="text-lg font-semibold">
                {t("workday.prep.empty_title")}
              </h2>
              <p class="max-w-md text-sm text-base-content/60">
                {t("workday.prep.empty_help")}
              </p>
              <button
                class="btn btn-primary btn-sm"
                onClick={() => (editing.value = null)}
              >
                {t("workday.prep.new")}
              </button>
            </div>
          ) : (
            <div class="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {shown.value.map((l) => {
                const days = scheduleDays(
                  l.schedule_days,
                  {
                    everyDay: t("workday.schedule.every_day"),
                    weekdays: t("workday.schedule.weekdays"),
                    weekends: t("workday.schedule.weekends"),
                  },
                  names,
                );
                return (
                  <div
                    key={l.id}
                    class={[
                      "flex flex-col rounded-xl border border-base-300 bg-base-100 p-5 shadow-sm",
                      l.active ? "" : "opacity-60",
                    ].join(" ")}
                  >
                    <div class="flex items-start justify-between gap-2">
                      <div>
                        <h3 class="text-lg font-bold">{l.name}</h3>
                        <div class="text-sm text-base-content/60">
                          {t(`workday.department.${l.department}`)}
                        </div>
                      </div>
                      <div class="dropdown dropdown-end">
                        <div
                          tabindex={0}
                          role="button"
                          class="btn btn-sm btn-outline shrink-0 flex-nowrap whitespace-nowrap border-base-300"
                        >
                          {t("workday.common.actions")}
                          <span class="material-symbols-outlined text-base">
                            expand_more
                          </span>
                        </div>
                        <ul
                          tabindex={0}
                          class="menu dropdown-content z-20 w-44 rounded-box border border-base-300 bg-base-100 p-1 shadow-lg"
                        >
                          <li>
                            <a onClick={() => (editing.value = l.id)}>
                              <span class="material-symbols-outlined text-base">
                                edit
                              </span>
                              {t("workday.common.edit")}
                            </a>
                          </li>
                          <li>
                            <a class="text-error" onClick={() => remove(l)}>
                              <span class="material-symbols-outlined text-base">
                                delete
                              </span>
                              {t("workday.common.delete")}
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <p class="mt-3 line-clamp-2 text-sm text-base-content/70">
                      {(l.item_names ?? []).join(" · ")}
                    </p>
                    <div class="mt-3 space-y-1 text-sm text-base-content/60">
                      <div class="flex items-center gap-2">
                        <span class="material-symbols-outlined text-base">
                          calendar_month
                        </span>
                        {days ?? t("workday.schedule.on_demand")}
                      </div>
                      <div class="flex items-center gap-2">
                        <span class="material-symbols-outlined text-base">
                          format_list_bulleted
                        </span>
                        {t("workday.prep.item_count").replace(
                          "{count}",
                          String(l.item_count),
                        )}
                      </div>
                    </div>
                    <div class="mt-auto border-t border-base-300 pt-4 mt-5 text-right">
                      <button
                        class="btn btn-primary btn-sm"
                        onClick={() => (running.value = l.id)}
                      >
                        <span class="material-symbols-outlined text-base">
                          play_arrow
                        </span>
                        {t("workday.prep.start_today")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {editing.value !== undefined && (
          <PrepListEditor
            listId={editing.value}
            onClose={async (changed: boolean) => {
              editing.value = undefined;
              if (changed) await load();
            }}
          />
        )}
        {running.value != null && (
          <PrepRunner
            listId={running.value}
            date={localDate()}
            onClose={() => (running.value = null)}
          />
        )}
      </div>
    );
  },
});
